import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData } from "@tanstack/react-query";
import { Box, Theme, useMediaQuery } from "@mui/material";
import { InvestmentQuoteNotationEnum } from "@rentablo/api/apiv1";
import { useInfiniteListAndSearchAllBookings } from "@api/v1/hooks/useBookings";
import { RangeSelectorControlProps, useTableSort } from "@hooks";
import { TableSummary } from "@components";
import { AutoTable, LoadingPlaceHolder } from "@rentablo/components";
import { type BookingTypeFilter } from "@components/MobileList";
import { useInvestmentBookingsTableConfig } from "./useInvestmentBookingsTableConfig";
import { InvestmentProtocolMobileList } from "./InvestmentProtocolMobile";
import { toTableBookingData } from "./toTableBookingData";

const PER_PAGE = 50;

interface InvestmentProtocolContainerProps {
  investmentId: number;
  investmentQuote: number;
  currentMarketValue: number;
  currencySymbol?: string;
  quoteNotation?: InvestmentQuoteNotationEnum;
  rangeProps: RangeSelectorControlProps;
}

export const InvestmentProtocolContainer = ({
  investmentId,
  investmentQuote,
  currencySymbol,
  currentMarketValue,
  quoteNotation,
  rangeProps,
}: InvestmentProtocolContainerProps) => {
  const { t } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [types, setTypes] = useState<BookingTypeFilter[]>([]);

  const { sort, sortField, sortDirection } = useTableSort({
    field: "valueDate",
    direction: "DESC",
  });

  const config = useInvestmentBookingsTableConfig({
    sortField,
    sortDirection,
    onSort: sort,
  });

  const phoneSortField = sortField === "amount" ? "amount" : "valueDate";
  const phoneSortDirection = sortDirection === "ASC" ? "ASC" : "DESC";
  const order = isSm
    ? [`${phoneSortField} ${phoneSortDirection.toLowerCase()}`]
    : sortField && sortDirection
      ? [`${sortField} ${sortDirection.toLowerCase()}`]
      : undefined;

  const {
    data,
    isPending,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteListAndSearchAllBookings(
    {
      investmentId: [investmentId],
      type: isSm && types.length ? types : undefined,
      minDate: rangeProps.range.startDate?.toISOString(),
      maxDate: rangeProps.range.endDate.toISOString(),
      order,
      perPage: PER_PAGE,
      displayCurrency: currencySymbol,
    },
    {
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      getNextPageParam: (lastPage) => {
        const page = lastPage.paging?.page ?? 0;
        const pageCount = lastPage.paging?.pageCount ?? 0;
        return page < pageCount ? page + 1 : undefined;
      },
    }
  );

  const bookings = useMemo(
    () =>
      toTableBookingData(
        (data?.pages ?? []).flatMap((p) => p.bookings ?? []),
        investmentQuote,
        quoteNotation
      ),
    [data, investmentQuote, quoteNotation]
  );

  if (isPending) {
    return (
      <Box mt={6}>
        <LoadingPlaceHolder />
      </Box>
    );
  }

  if (isSm) {
    return (
      <Box pt={6} data-testid="booking-table-container">
        <InvestmentProtocolMobileList
          bookings={bookings}
          totalCount={data?.pages[0]?.paging?.totalCount}
          rangeProps={rangeProps}
          types={types}
          onTypesChange={setTypes}
          sortField={phoneSortField}
          sortDirection={phoneSortDirection}
          onSort={sort}
          isRefreshing={isFetching && !isFetchingNextPage}
          hasMore={Boolean(hasNextPage)}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          showEndOfList={(data?.pages.length ?? 0) > 1}
        />
      </Box>
    );
  }

  return (
    <Box pt={6} pb={6} data-testid="booking-table-container">
      <TableSummary
        summaryValue={currentMarketValue}
        currencySymbol={currencySymbol}
        rangeProps={rangeProps}
        hideMobileRangeSelector
      />
      <Box data-testid="booking-table" mt={1}>
        <AutoTable
          settings={config}
          data={bookings}
          additionalProps={{ currencySymbol }}
          isFetching={isFetching && !isFetchingNextPage}
          paging={{
            type: "infinite",
            hasMore: Boolean(hasNextPage),
            next: () => {
              fetchNextPage();
            },
            loader: <LoadingPlaceHolder />,
          }}
          showNoData={bookings.length === 0}
          noDataFirstLine={t("investment.protocolTable.noBookings")}
        />
      </Box>
    </Box>
  );
};
